import React, { useState, useEffect } from 'react';
import { Calendar, ExternalLink, Linkedin, Clock, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';

const Blog = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = async () => {
        try {
            const response = await fetch('/.netlify/functions/blog-posts');
            if (response.ok) {
                const data = await response.json();
                setPosts(data.posts || []);
            }
        } catch (error) {
            console.error('Error fetching posts:', error);
            // Fallback to sample posts for development
            setPosts([
                {
                    id: '20241216-quality-roi',
                    title: 'Why Quality Metrics Should Map Directly to Revenue',
                    excerpt: 'The gap between quality engineering and business outcomes is costing companies millions. Here\'s how to bridge it.',
                    content: '',
                    publishedAt: '2024-12-16T10:00:00Z',
                    linkedinPosted: true,
                    readTime: 5
                },
                {
                    id: '20241215-qa-leadership',
                    title: 'From Test Coverage to Business Coverage: A New Paradigm',
                    excerpt: 'Technical metrics like code coverage tell one story. Business metrics tell another. The best QA leaders speak both languages.',
                    content: '',
                    publishedAt: '2024-12-15T14:00:00Z',
                    linkedinPosted: true,
                    readTime: 7
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <>
            <Navigation scrolled={scrolled} />

            <section className="min-h-screen pt-32 pb-20 relative">
                {/* Background */}
                <div className="absolute inset-0 circuit-pattern opacity-20" />
                <div className="absolute inset-0 grid-pattern opacity-10" />

                <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <Link
                            to="/"
                            className="inline-flex items-center text-primary-400 hover:text-primary-300 transition-colors mb-8"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Home
                        </Link>

                        <h1 className="text-4xl md:text-6xl font-bold mb-6">
                            <span className="gradient-text">IronGate Blog</span>
                        </h1>
                        <p className="text-xl text-secondary max-w-2xl mx-auto">
                            Insights on quality engineering, QA leadership, and turning technical metrics into business impact.
                        </p>
                    </div>

                    {/* Posts Grid */}
                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-20">
                            <p className="text-secondary text-lg">No blog posts yet. Check back soon!</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 gap-8">
                            {posts.map((post) => (
                                <Link
                                    to={`/blog/${post.slug || post.id}`}
                                    key={post.id}
                                    className="block"
                                >
                                    <article
                                        className="metallic-texture gradient-border rounded-2xl p-8 backdrop-blur-sm relative overflow-hidden group hover:scale-[1.02] transition-transform cursor-pointer h-full"
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                                        <div className="relative z-10">
                                            {/* Meta info */}
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center space-x-4 text-sm text-secondary">
                                                    <span className="flex items-center">
                                                        <Calendar className="w-4 h-4 mr-1" />
                                                        {formatDate(post.publishedAt)}
                                                    </span>
                                                    <span className="flex items-center">
                                                        <Clock className="w-4 h-4 mr-1" />
                                                        {post.readTime || 5} min read
                                                    </span>
                                                </div>
                                                {post.linkedinPosted && (
                                                    <div className="flex items-center text-[#0A66C2]" title="Shared on LinkedIn">
                                                        <Linkedin className="w-5 h-5" />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Title */}
                                            <h2 className="text-2xl font-bold text-primary mb-4 group-hover:text-primary-400 transition-colors">
                                                {post.title}
                                            </h2>

                                            {/* Excerpt */}
                                            <p className="text-secondary mb-6 line-clamp-3">
                                                {post.excerpt}
                                            </p>

                                            {/* Read more */}
                                            <span className="inline-flex items-center text-primary-400 group-hover:text-primary-300 font-medium transition-colors">
                                                Read Article
                                                <ExternalLink className="w-4 h-4 ml-2" />
                                            </span>
                                        </div>
                                    </article>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </>
    );
};

export default Blog;
